const initializeFilazooAnnouncementGradients = (root = document) => {
  root.querySelectorAll('[data-filazoo-announcement-gradient-bar]').forEach((bar) => {
    if (bar.dataset.filazooAnnouncementGradientReady === 'true') return;

    const layers = Array.from(bar.querySelectorAll(':scope > .filazoo-announcement-gradient'));
    const slides = Array.from(bar.querySelectorAll('.slideshow__slide'));
    const singleAnnouncement = bar.querySelector('.announcement-bar[style*="--filazoo-announcement-slide-gradient"]');

    if (layers.length !== 2 || (!slides.length && !singleAnnouncement)) return;

    let activeLayerIndex = 0;
    let currentGradient = '';

    const getGradient = (element) =>
      window.getComputedStyle(element).getPropertyValue('--filazoo-announcement-slide-gradient').trim();

    const getForeground = (element) =>
      window.getComputedStyle(element).getPropertyValue('--filazoo-announcement-slide-foreground').trim();

    const showGradient = (element, immediate = false) => {
      const gradient = getGradient(element);
      const foreground = getForeground(element);

      if (foreground) bar.style.setProperty('--filazoo-announcement-foreground', foreground);
      if (!gradient || gradient === currentGradient) return;

      currentGradient = gradient;

      if (immediate) {
        layers[0].style.background = gradient;
        layers[0].classList.add('is-active');
        layers[1].classList.remove('is-active');
        activeLayerIndex = 0;
        return;
      }

      const nextLayerIndex = activeLayerIndex === 0 ? 1 : 0;
      layers[nextLayerIndex].style.background = gradient;
      layers[nextLayerIndex].classList.add('is-active');
      layers[activeLayerIndex].classList.remove('is-active');
      activeLayerIndex = nextLayerIndex;
    };

    if (slides.length) {
      showGradient(slides.find((slide) => slide.getAttribute('aria-hidden') !== 'true') || slides[0], true);

      const observer = new MutationObserver((mutations) => {
        const activeSlide = mutations
          .map((mutation) => mutation.target)
          .find((slide) => slide.getAttribute('aria-hidden') === 'false');

        if (activeSlide) showGradient(activeSlide);
      });

      slides.forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] }));
    } else {
      showGradient(singleAnnouncement, true);
    }

    bar.dataset.filazooAnnouncementGradientReady = 'true';
  });
};

const initializeFilazooAnnouncementMarquees = (root = document) => {
  root.querySelectorAll('[data-filazoo-announcement-gradient-bar]').forEach((bar) => {
    if (bar.dataset.filazooAnnouncementMarqueeReady === 'true') return;

    const messages = Array.from(bar.querySelectorAll('.announcement-bar__message'));
    const mobileMedia = window.matchMedia('(max-width: 749px)');

    const updateMarquees = () => {
      messages.forEach((message) => {
        const item = message.querySelector('.announcement-bar__message-item:first-child');
        if (!item) return;

        message.classList.remove('is-overflowing');
        message.style.removeProperty('--filazoo-marquee-duration');

        if (!mobileMedia.matches || item.scrollWidth <= message.clientWidth) return;

        const duration = Math.max(10, (item.scrollWidth + 40) / 35);
        message.style.setProperty('--filazoo-marquee-duration', `${duration.toFixed(2)}s`);
        message.classList.add('is-overflowing');
      });
    };

    requestAnimationFrame(updateMarquees);
    if (document.fonts?.ready) document.fonts.ready.then(updateMarquees);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateMarquees);
      resizeObserver.observe(bar);
    } else {
      window.addEventListener('resize', updateMarquees);
    }

    if ('addEventListener' in mobileMedia) {
      mobileMedia.addEventListener('change', updateMarquees);
    } else {
      mobileMedia.addListener(updateMarquees);
    }

    bar.dataset.filazooAnnouncementMarqueeReady = 'true';
  });
};

const initializeFilazooMobileAnnouncementSliders = (root = document) => {
  root.querySelectorAll('[data-filazoo-announcement-gradient-bar]').forEach((bar) => {
    const slideshow = bar.querySelector('slideshow-component');
    const slider = slideshow?.querySelector('.announcement-bar-slider .slider');
    const slides = Array.from(slider?.querySelectorAll('.slideshow__slide') || []);

    if (!slideshow || slides.length < 2 || slideshow.filazooMobileSlider) return;

    const mobileMedia = window.matchMedia('(max-width: 749px)');
    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true')
    );
    let rotationInterval;

    const stopRotation = () => window.clearInterval(rotationInterval);

    const showSlide = (index) => {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-filazoo-mobile-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('tabindex', !isActive);
        slide.querySelectorAll('a').forEach((link) => {
          if (isActive) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
      });
    };

    const startRotation = () => {
      stopRotation();
      const wasMobile = bar.classList.contains('filazoo-mobile-announcement-carousel');

      if (!mobileMedia.matches) {
        bar.classList.remove('filazoo-mobile-announcement-carousel');
        slides.forEach((slide) => slide.classList.remove('is-filazoo-mobile-active'));

        if (wasMobile) {
          slider.scrollTo({ left: slides[activeIndex].offsetLeft });
          if (slider.dataset.autoplay === 'true') slideshow.play?.();
        }
        return;
      }

      slideshow.pause?.();
      bar.classList.add('filazoo-mobile-announcement-carousel');
      showSlide(activeIndex);

      if (slider.dataset.autoplay === 'true') {
        const speed = Math.max(Number(slider.dataset.speed) || 5, 3) * 1000;
        rotationInterval = window.setInterval(() => showSlide(activeIndex + 1), speed);
      }
    };

    slideshow.filazooMobileSlider = { showSlide, startRotation, stopRotation, slides };
    mobileMedia.addEventListener?.('change', startRotation);
    startRotation();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeFilazooAnnouncementGradients();
    initializeFilazooAnnouncementMarquees();
    initializeFilazooMobileAnnouncementSliders();
  });
} else {
  initializeFilazooAnnouncementGradients();
  initializeFilazooAnnouncementMarquees();
  initializeFilazooMobileAnnouncementSliders();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeFilazooAnnouncementGradients(event.target);
  initializeFilazooAnnouncementMarquees(event.target);
  initializeFilazooMobileAnnouncementSliders(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('.slideshow__slide');
  const controller = slide?.closest('slideshow-component')?.filazooMobileSlider;
  if (!controller) return;

  const selectedIndex = controller.slides.indexOf(slide);
  if (selectedIndex < 0) return;
  controller.stopRotation();
  controller.showSlide(selectedIndex);
});

document.addEventListener('shopify:block:deselect', (event) => {
  event.target.closest?.('slideshow-component')?.filazooMobileSlider?.startRotation();
});
